/**
 * One source's call funnel (owner D8: "a bug can drop real findings"). Template example: NextAdmin
 * `(dashboard)/(home)/_component/top-products` (a `Card` of `ItemList` rows).
 */
import type React from 'react';
import { Badge, Card, ItemList, type ItemListItem } from '../../../components/ui/index.js';
import type { ReasoningCallFunnel } from '../../../generated/api/types.gen.js';
import { VERDICT_LABEL } from './reasoning-format.js';

const share = (part: number, whole: number) => (whole > 0 ? ` (${Math.round((part / whole) * 100)} %)` : '');

const breakdown = (counts: Record<string, number>, labels: Record<string, string> = {}) =>
  Object.entries(counts)
    .sort(([, a], [, b]) => b - a)
    .map(([key, n]) => `${labels[key] ?? key} ${n}`)
    .join(' · ') || 'none';

export const ReasoningCallFunnelCard: React.FC<{ funnel: ReasoningCallFunnel }> = ({ funnel }) => {
  const live = funnel.calls - funnel.callsDismissed;
  const notRead = funnel.itemsByContract['not read'] ?? 0;
  const items: ItemListItem[] = [
    {
      id: 'items',
      title: 'Items analysed',
      subtitle: `Read by prompt version: ${breakdown(funnel.itemsByContract)}`,
      value: funnel.itemsAnalysed,
    },
    {
      id: 'failed',
      title: 'Items whose call pass failed',
      subtitle: 'Read again only when an operator clears them',
      value: funnel.itemsFailed > 0 ? <Badge variant="danger">{funnel.itemsFailed}</Badge> : 0,
    },
    {
      id: 'calls',
      title: 'Calls',
      subtitle: `${funnel.callsDismissed} dismissed (an earlier read of a re-read item); ${live} live`,
      value: funnel.calls,
    },
    {
      id: 'subject',
      title: 'Live calls without a subject',
      subtitle: 'Never checked against prices: no instrument was resolved',
      value: `${funnel.callsWithoutSubject}${share(funnel.callsWithoutSubject, live)}`,
    },
    {
      id: 'horizon',
      title: 'Live calls with a stated horizon',
      subtitle: `The rest are untestable for the track record; ${funnel.callsConditional} are conditional`,
      value: `${funnel.callsHorizonStated}${share(funnel.callsHorizonStated, live)}`,
    },
    {
      id: 'reasons',
      title: 'Reasons of live calls',
      subtitle: `${funnel.reasonsStated} stated, ${funnel.reasonsCued} read through a cue`,
      value: funnel.reasons,
    },
    {
      id: 'reason-checks',
      title: 'Reason checks (latest)',
      subtitle: breakdown(funnel.reasonChecks, VERDICT_LABEL),
      value: Object.values(funnel.reasonChecks).reduce((sum, n) => sum + n, 0),
    },
    {
      id: 'outcomes',
      title: 'Outcomes (latest)',
      subtitle: breakdown(funnel.outcomes, VERDICT_LABEL),
      value: Object.values(funnel.outcomes).reduce((sum, n) => sum + n, 0),
    },
    ...funnel.undecided.map((entry, index) => ({
      id: `undecided-${index}`,
      title: 'Outcome not decided',
      subtitle: entry.reason,
      value: entry.count,
    })),
  ];
  return (
    <Card
      title={funnel.handle}
      actions={notRead > 0 ? <Badge variant="warning">{notRead} not read yet</Badge> : undefined}
      className="xl:col-span-6"
    >
      <ItemList items={items} />
    </Card>
  );
};
